/*
 * Copyright (c) 2018 Integral Agile, Inc. All rights reserved.
 *   Use of this file is subject to the terms and conditions defined in the file
 * 'LICENSE.txt'
 *   which is distributed in the root directory of this source code tree.
 */

import Jsona from 'jsona';
import * as API from '../utils/api';
import Types from '../constants/actionTypes';
import Store from '../store/store';
import { applicationError } from './notificationActions';
import {
  fetchAllWorkPackages,
  setSelectedSpace,
  removeSpace
} from './workPackageActions';
import { fetchAllTasks } from './taskActions';
import { fetchUsers } from './userActions';
import { getSelectedSpace, getOrganizations } from '../utils/reduxHelpers';

const dataFormatter = new Jsona();
const cadenceId = () => Store.getState().cadenceReducer?.cadence?.id;

export function updateTeamStore(team, action) {
  const members = dataFormatter.deserialize(team.members);
  // const selectedSpace = getSelectedSpace();
  const organizations = getOrganizations();

  // this check determines whether or not a user should process an incoming team broadcast
  if (!organizations.some((org) => org.id === team.organization_id))
    return (dispatch) => {};
  switch (action) {
    case 'create':
      return (dispatch) => {
        dispatch({
          type: Types.ADD_TEAM,
          team: { ...team, members }
        });
      };
    case 'update':
    case 'destroy':
      return (dispatch) => {
        // get updated team with permissions for current user
        dispatch(individualTeamRetrieval(team.id));
        dispatch(fetchUsers());
      };

    // return (dispatch) => {
    //   dispatch({
    //     type: Types.UPDATE_TEAM,
    //     team: { ...team, members }
    //   });
    //   if (selectedSpace.type === 'team' && selectedSpace.id === team.id) {
    //     dispatch({
    //       type: Types.UPDATE_SELECTED_TEAM_SPACE,
    //       team: { ...team, members }
    //     });
    //   }
    //   dispatch(fetchUsers());
    //   dispatch({
    //     type: Types.UPDATE_PRODUCT_TEAMS,
    //     team: { ...team, members }
    //   });
    //   dispatch({
    //     type: Types.UPDATE_TEAM_DEFAULT_OPTIONS,
    //     team: { ...team, members }
    //   });
    // };
    default:
      return (dispatch) => {};
  }
}

function individualTeamRetrieval(updatedTeamId) {
  return (dispatch) => {
    dispatch({ type: Types.REQUEST_TEAMS });
    return API.get(`teams/${updatedTeamId}/individual_team`)
      .then((response) => {
        const team = dataFormatter.deserialize(response);
        dispatch({ type: Types.UPDATE_TEAM, team });
        dispatch({ type: Types.UPDATE_PRODUCT_TEAMS, team });
        dispatch({ type: Types.UPDATE_TEAM_DEFAULT_OPTIONS, team });
      })
      .catch((error) => {
        dispatch(applicationError(error));
        dispatch({ type: Types.REQUEST_TEAMS_FAILED });
      });
  };
}

const resolveTeam = (team) => {
  const { products } = Store.getState().productReducer;
  const { users } = Store.getState().userReducer;
  team.products = products.filter((p) =>
    team.product_ids.some((pid) => pid === p.id)
  );
  team.members = users.filter((u) =>
    team.member_ids.some((mid) => mid === u.id)
  );
  team.deleted_members = users.filter((u) =>
    team.deleted_member_ids.some((mid) => mid === u.id)
  );
};

export function fetchTeams() {
  return (dispatch) => {
    dispatch({ type: Types.REQUEST_TEAMS });
    return API.get('teams')
      .then((response) => {
        const teams = dataFormatter.deserialize(response);
        teams.forEach((team) => {
          resolveTeam(team);
        });
        dispatch({ type: Types.RECEIVE_TEAMS, teams });
      })
      .catch((error) => {
        dispatch(applicationError(error));
        dispatch({ type: Types.REQUEST_TEAMS_FAILED });
      });
  };
}

export function createTeam(data, organizationId) {
  return (dispatch) => {
    dispatch({ type: Types.REQUEST_TEAM_CREATE });
    return API.post('teams', { ...data, organization_id: organizationId })
      .then((response) => response.json().then((body) => body))
      .then((response) => {
        const team = dataFormatter.deserialize(response);
        if (data.image && data.image.droppedFile) {
          const uploadData = new FormData();
          const fileCopy = data.image.droppedFile;
          fileCopy.path = data.image.droppedFileUrl;
          uploadData.append('team[image]', fileCopy);
          uploadData.append('team[id]', team.id);
          dispatch({ type: Types.UPLOAD_TEAM_PICTURE });
          return API.filePost(
            `teams/${team.id}/upload_picture`,
            uploadData,
            'PUT'
          )
            .then((uploadResponse) => uploadResponse.json())
            .then((uploadResponse) =>
              dispatch({ type: Types.ADD_TEAM, team: uploadResponse.team })
            )
            .catch((error) => {
              dispatch(applicationError(error));
            });
        }
        dispatch({ type: Types.ADD_TEAM, team });
        const payload = { team, organizationId };
        dispatch({ type: Types.ADD_TEAM_TO_ORGANIZATION, payload });
        if (!Store.getState().workPackageReducer.spaceSelected)
          dispatch(setSelectedSpace(team, 'team'));
      })
      .catch((error) => dispatch(applicationError(error)));
  };
}

function updateSelectedTeamSpace(team) {
  const selectedSpace = JSON.parse(localStorage.getItem('selectedSpace'));
  if (
    !selectedSpace ||
    selectedSpace.initialSpace ||
    selectedSpace.type === 'product'
  )
    return false;
  if (selectedSpace.id !== team.id) return false;
  return true;
}

function updateSelectedProductSpace(team) {
  const { selectedSpace } = Store.getState().workPackageReducer;
  if (
    !selectedSpace ||
    selectedSpace.initialSpace ||
    selectedSpace.type === 'team'
  )
    return false;

  if (selectedSpace.teams.length < 1) return false;
  const teamToUpdate = selectedSpace.teams.find((t) => t.id === team.id);
  if (!teamToUpdate) return false;
  return true;
}

export function updateTeam(teamId, data) {
  return (dispatch) => {
    dispatch({ type: Types.REQUEST_TEAM_UPDATE });
    const { members, products, ...attrs } = data;
    const payload = { ...attrs, member_ids: members.map((m) => m.id) };    
    return API.put(`teams/${teamId}`, payload)
      .then((response) => response.json().then((body) => body))
      .then((response) => {
        const team = dataFormatter.deserialize(response);
        dispatch({ type: Types.UPDATE_TEAM, team }); // updates teamReducer
        dispatch({
          type: Types.UPDATE_TEAM_DEFAULT_OPTIONS,
          team
        });
        dispatch({
          type: Types.UPDATE_PRODUCT_TEAMS,
          team
        }); // update teams associated with products in the productReducer

        if (data.kanban !== undefined && data.kanban !== null) {
          dispatch({
            type: Types.UPDATE_WORK_PACKAGE_LIST,
            cadenceId: cadenceId()
          });
        }

        if (updateSelectedTeamSpace(team)) {
          dispatch({
            type: Types.UPDATE_SELECTED_TEAM_SPACE,
            team
          });
        }
        if (updateSelectedProductSpace(team)) {
          dispatch({
            type: Types.UPDATE_SELECTED_PRODUCT_SPACE,
            team
          });
        }
      })
      .catch((error) => dispatch(applicationError(error)));
  };
}

export function addRole(teamId, userId, roleType) {
  const currentUser = Store.getState().userReducer.user;
  return (dispatch) => {
    dispatch({ type: Types.ROLE_CHANGE, payload: true });
    return API.put(`teams/${teamId}/add_role`, {
      user_id: userId,
      role_type: roleType
    })
      .then((response) => response.json().then((body) => body))
      .then((response) => {
        const team = dataFormatter.deserialize(response);
        const updatedUser = team.members.find((member) => member.id === userId);
        dispatch({ type: Types.UPDATE_USER, user: updatedUser });
        dispatch({ type: Types.UPDATE_TEAM, team });
        if (userId === currentUser.id) {
          const updatedUser = team.members.find(
            (member) => member.id === currentUser.id
          );
          dispatch({ type: Types.UPDATE_USER_ROLES, updatedUser });
        }
        dispatch({ type: Types.ROLE_CHANGE, payload: false });
      })
      .catch((error) => dispatch(applicationError(error)));
  };
}

export function removeRole(teamId, roleId, userId) {
  const currentUser = Store.getState().userReducer.user || {};
  const { roles = [] } = currentUser;
  return (dispatch) => {
    dispatch({ type: Types.ROLE_CHANGE, payload: true });
    return API.put(`teams/${teamId}/remove_role`, { role_id: roleId })
      .then((response) => response.json().then((body) => body))
      .then((response) => {
        const team = dataFormatter.deserialize(response);
        const updatedUser = team.members.find((member) => member.id === userId);
        dispatch({ type: Types.UPDATE_USER, user: updatedUser });
        dispatch({ type: Types.UPDATE_TEAM, team });
        if (roles.findIndex((role) => role.id === roleId) > -1) {
          const updatedUser = team.members.find(
            (member) => member.id === currentUser.id
          );
          dispatch({ type: Types.UPDATE_USER_ROLES, updatedUser });
        }
        dispatch({ type: Types.ROLE_CHANGE, payload: false });
      })
      .catch((error) => dispatch(applicationError(error)));
  };
}

export function deleteTeam(team) {
  return (dispatch) => {
    dispatch({ type: Types.REQUEST_TEAM_UPDATE });
    return API.del(`teams/${team.id}`)
      .then(() => dispatch({ type: Types.DELETE_TEAM, team }))
      .then(() => {
        dispatch(removeSpace(team.id, 'team'));
      })
      .then(() => dispatch(fetchTeams()))
      .catch((error) => dispatch(applicationError(error)));
  };
}

export function restoreTeam(team) {
  return (dispatch) => {
    dispatch({ type: Types.REQUEST_TEAM_UPDATE });
    return API.put(`teams/${team.id}/restore`)
      .then((response) => response.json())
      .then((response) => {
        const team = dataFormatter.deserialize(response);
        dispatch({ type: Types.UPDATE_TEAM, team });
      })
      .catch((error) => dispatch(applicationError(error)));
  };
}

export function removeMember(teamId, memberId) {
  return (dispatch) => {
    dispatch({ type: Types.REQUEST_TEAMS });
    return API.del(`teams/${teamId}/remove_member`, { member_id: memberId })
      .then((response) => response.json().then((body) => body))
      .then((response) => {
        const team = dataFormatter.deserialize(response);
        const removedMember = team.deleted_members.find(
          (m) => m.id === memberId
        );
        const { teams } = Store.getState().teamReducer;
        removedMember.teams = teams.filter((t) =>
          // eslint-disable-next-line eqeqeq
          removedMember.teams.some((mt) => mt.id == t.id)
        );
        dispatch({ type: Types.UPDATE_TEAM, team });
        // User's default team has been updated by backend, so update
        dispatch({ type: Types.UPDATE_USER, user: removedMember });
        dispatch({ type: Types.UPDATE_TEAM_MEMBER, user: removedMember });
      })
      .then(() => {
        dispatch(fetchAllWorkPackages());
        dispatch(fetchAllTasks());
      })
      .catch((error) => dispatch(applicationError(error)));
  };
}

export function restoreMember(teamId, memberId) {
  return (dispatch) => {
    dispatch({ type: Types.REQUEST_TEAMS });
    return API.put(`teams/${teamId}/restore_member`, { member_id: memberId })
      .then((response) => response.json().then((body) => body))
      .then((response) => {
        const team = dataFormatter.deserialize(response);
        const restoredMember = team.members.find((m) => m.id === memberId);
        const { teams } = Store.getState().teamReducer;
        restoredMember.teams = teams.filter((t) =>
          // eslint-disable-next-line eqeqeq
          restoredMember.teams.some((mt) => mt.id == t.id)
        );
        dispatch({ type: Types.UPDATE_TEAM, team });
        // User's default team has been updated by backend, so update
        dispatch({ type: Types.UPDATE_USER, user: restoredMember });
        dispatch({ type: Types.UPDATE_TEAM_MEMBER, user: restoredMember });
      })
      .then(() => {
        dispatch(fetchAllWorkPackages());
        dispatch(fetchAllTasks());
      })
      .catch((error) => dispatch(applicationError(error)));
  };
}

export function uploadTeamImage(file, fileUrl, teamId) {
  const data = new FormData();
  const fileCopy = file;
  fileCopy.path = fileUrl;
  data.append('team[image]', fileCopy);
  data.append('team[id]', teamId);
  return (dispatch) => {
    dispatch({ type: Types.UPLOAD_TEAM_PICTURE });
    return API.filePost(`teams/${teamId}/upload_picture`, data, 'PUT')
      .then((response) => response.json())
      .then((response) => {
        const team = dataFormatter.deserialize(response);
        dispatch({ type: Types.UPDATE_TEAM, team });
      })
      .catch((error) => {
        dispatch(applicationError(error));
      });
  };
}

export function addTeam(team) {
  return (dispatch) => dispatch({ type: Types.ADD_NEW_TEAM, team });
}

export function clearTeamData() {
  return (dispatch) => dispatch({ type: Types.CLEAR_NEW_TEAM });
}

export function getBurndownData(teamId, cadenceId) {
  return (dispatch) => {
    dispatch({
      teamId,
      type: Types.REQUEST_TEAM_GRAPHS,
      graph: 'burndown'
    });
    return API.get(`cadences/${cadenceId}/burndown`)
      .then((response) =>
        dispatch({
          teamId,
          type: Types.ADD_TEAM_METRICS_BURNDOWN,
          burndown: response
        })
      )
      .catch((error) => dispatch(applicationError(error)));
  };
}

export function getCumulativeFlowSeries(teamId, startDate, endDate) {
  return (dispatch) => {
    dispatch({
      teamId,
      type: Types.REQUEST_TEAM_GRAPHS,
      graph: 'cumulative'
    });
    return API.get(
      `teams/${teamId}/cumulative-flow-series?start=${startDate}&end=${endDate}`
    )
      .then((response) =>
        dispatch({
          teamId,
          type: Types.ADD_TEAM_METRICS_CUMULATIVE,
          cumulative: response.data
        })
      )
      .catch((error) => dispatch(applicationError(error)));
  };
}

export function getLeanSeries(
  teamId,
  resultType = { risk: '', value: '', size: '' }
) {
  return (dispatch) => {
    dispatch({
      teamId,
      type: Types.REQUEST_TEAM_GRAPHS,
      graph: 'lean'
    });
    return API.get(
      `teams/${teamId}/lean-metrics`,
      `risk=${resultType.risk}&size=${resultType.size}&value=${resultType.value}`,
      false
    )
      .then((response) =>
        dispatch({
          teamId,
          type: Types.ADD_TEAM_METRICS_LEAN,
          lean: response.data
        })
      )
      .catch((error) => dispatch(applicationError(error)));
  };
}

export function getVelocityData(teamId, sprints) {
  return (dispatch) => {
    dispatch({
      teamId,
      type: Types.REQUEST_TEAM_GRAPHS,
      graph: 'velocity'
    });
    return API.get(`teams/${teamId}/velocity-graph?sprints=${sprints}`)
      .then((response) =>
        dispatch({
          teamId,
          type: Types.ADD_TEAM_METRICS_VELOCITY,
          velocity: response.data
        })
      )
      .catch((error) => dispatch(applicationError(error)));
  };
}
